import { Box, useQuery } from "@chakra-ui/react";
import { useState, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { HeaderSearch } from "styled/SectionContainer.styled";
import Swal from "sweetalert2";
import { useConfirmationModal } from "hooks/useConfirmationModal";
import Vapor from "laravel-vapor";
import environment from "constants/apiConst";
import { AlertErrorAlert } from "components/Alerts/AlertErrorAlert";
import {
  isNonEmptyString,
  isObject,
  isObjectNotEmpty,
  isString,
} from "utils/type-check-utils";
import IntegracionBoxCrudContex from "context/IntegracionBoxCrudContex";
import { useGlobalContext } from "context/GlobalContext";
import { ModalGeneric } from "components/modal/ModalGeneric";
import { GroupInputUpload } from "pages/Entry_and_exit_forms/GroupInputUpload";
import { Storage } from "hooks/useStorage";
import ExistingFiles from "./ExistingFiles";
import DocumentUploader from "./DocumentUploader";
import { __DeleteAttachment } from "request/Pases/__CrudPass";
import EnhancedFileUploadManager from "./MultipleFileUploader";

/**
 * DocumentManager - Componente que maneja la gestión de documentos para el formulario.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.id - ID del formulario.
 * @param {Object} props.data - Datos del formulario.
 * @param {string} props.envShowForm - Endpoint para obtener los datos del formulario.
 * @param {string} props.envDeleteAttachment - Endpoint para eliminar un archivo adjunto.
 * @param {string} props.envUpdateForm - Endpoint para actualizar el formulario.
 * @param {string} props.envDeleteOtherAttachment - Endpoint para eliminar un archivo adjunto.
 */
const DocumentManager = ({
  id,
  data,
  envShowForm,
  envDeleteAttachment,
  envUpdateForm,
  envDeleteOtherAttachment,
  documentPreview = false,
  viewBtn = false,
}) => {
  const { requestData } = useGlobalContext();
  const token = useSelector((state) => state.auth.dataUser.token);
  const [openAlert] = AlertErrorAlert();

  const [attachmentDescription, setAttachmentDescription] = useState([]);
  const [attachmentNumber, setAttachmentNumber] = useState([]);
  const [files, setFiles] = useState({});
  const [nameFiles, setNameFiles] = useState([]);
  const [attachment, setAttachment] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [otherDocuments, setOtherDocuments] = useState([]);

  const [isUploadActive, setIsUploadActive] = useState(false);

  // const { deleteAttachment } = useContext(IntegracionBoxCrudContex);

  const { nameDocument } = useGlobalContext();

  const { mutate } = useMutation({
    mutationFn: requestData,
  });

  const { get: getStorage } = Storage;

  const extensionOptions = [
    ".doc",
    ".docx",
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".bmp",
    ".xls",
    ".xlsx",
    ".xlsm",
    "image/png",
    "image/*",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/pdf",
    "application/vnd.ms-excel",
  ];

  const {
    isLoading: isLoadingFiles,
    data: data_Files,
    refetch,
    isFetching,
    isSuccess,
  } = useQuery({
    queryKey: ["data_Files"],
    queryFn: () =>
      requestData({
        Endpoint: envShowForm,
        PropertyBody: "get",
        resourceIdentifier: id,
      }),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const updateFileState = (uploadResponse, name, idDocument, targetId) => {
    const uploadData = getStorage("uploadData")?.uploadData || {};
    const file = {
      [targetId]: {
        file: uploadResponse,
        name,
        document_id: idDocument,
        ...uploadData,
      },
    };

    if (isObjectNotEmpty(file)) updateAndCreate(file);

    return localStorage.removeItem("uploadData");
  };

  const extractDocumentId = (id) => {
    return Number(id.replace("document-", ""));
  };

  const updateNameFilesState = (name) => {
    setNameFiles((prevNameFiles) => [...prevNameFiles, { name }]);
  };

  const clearLocalStorageErrors = (idDocument) => {
    const errors = JSON.parse(
      localStorage.getItem(`errorAttadchment-${id}`) || "[]"
    );
    if (errors.includes(idDocument)) {
      const filteredErrors = errors.filter((e) => e !== idDocument);
      localStorage.setItem(
        `errorAttadchment-${id}`,
        JSON.stringify(filteredErrors)
      );
    }
  };

  function validateFileExtension(file) {
    const getFileExtension = file.name.split(".").pop();
    const fileExtension = isString(getFileExtension)
      ? getFileExtension.toLowerCase()
      : getFileExtension || "";

    const isValidExtension = extensionOptions.includes(`.${fileExtension}`);
    if (!isValidExtension) {
      openAlert(
        `El archivo ${file.name} no es un tipo de archivo permitido.`,
        "error"
      );
    }
    return isValidExtension;
  }

  async function uploadFile(file) {
    return Vapor.store(file, {
      baseURL: environment.VAPOR_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  const {
    closeModal: onCloseUpload,
    openModal: onOpenUpload,
    isOpen: isOpenUpload,
  } = useConfirmationModal();

  const handleFiles = (e, idFiles) => {
    e.preventDefault();
    document.getElementById(`${idFiles}`).click();
  };

  const handleFilesInput = async (e) => {
    setIsLoading(true);
    try {
      e.preventDefault();
      // const resModal = await onOpenUpload(); // se deshabilita para que no se muestre el modal de carga de archivos
      const { name, files: uploadedFiles, id } = e.target;
      const uploadData = getStorage("uploadData")?.uploadData || {};

      if (!uploadedFiles.length) return;
      if (uploadData?.cancel) return localStorage.removeItem("uploadData");

      const isValidExtension = validateFileExtension(uploadedFiles[0]);
      if (!isValidExtension) return;

      const idDocument = extractDocumentId(id);
      const uploadResponse = await uploadFile(uploadedFiles[0]);

      updateFileState(
        uploadResponse,
        uploadedFiles[0]?.name,
        idDocument,
        e.target.id
      );
      updateNameFilesState(name);
      clearLocalStorageErrors(idDocument);
      setIsLoading(false);
    } catch (error) {
      console.error("Error handling file input:", error);
      setIsLoading(false);
    }
  };

  const deleteAttachment = async (
    endpoint,
    id,
    data,
    set,
    newData,
    callBack = () => {}
  ) => {
    try {
      const res = await __DeleteAttachment(endpoint, token, data, id);
      if (res.data.status.code === 200) {
        openAlert(res?.data?.status?.message, "success");
        if (set) set(newData);
      } else {
        openAlert(res?.data?.status?.message, "error");
      }

      callBack();
    } catch (error) {
      console.log(error);
    }
  };

  const removeItemFromArr = (archivo, nombre) => {
    Swal.fire({
      title: `¿Estas seguro de eliminar el archivo "${files?.[archivo]?.name}"?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1F7AC3",
      confirmButtonText: "Eliminar",
      customClass: {
        title: "class-text",
      },
    }).then((resultado) => {
      if (resultado.isConfirmed) {
        if (files[archivo] !== undefined && files[archivo] !== null) {
          const obj = Object.assign({}, { ...files });
          if (obj[archivo] !== undefined && obj[archivo] !== null) {
            delete obj[archivo];
            if (files[archivo].from_server) {
              deleteAttachment(
                envDeleteAttachment,
                files[archivo].id,
                "",
                setFiles,
                obj,
                () => refetch()
              );
            } else {
              const fileInput = document.getElementById("OtherAttachment");
              if (fileInput) fileInput.value = "";
              setFiles(obj);
            }
          }
        }
      }
    });
  };

  const updateAndCreate = async (data = undefined, payloadManual = false) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("id", id);
    const dataAttachments = attachment[0];
    const vaporRes = (await data) || null;

    dataAttachments?.id &&
      formData.append("other_attachment_id[]", dataAttachments?.id);
    const file = {
      file: vaporRes,
    };

    if (dataAttachments?.id || dataAttachments?.other_attachment_description) {
      formData.append(
        "other_attachment[]",
        JSON.stringify({ ...file, name: dataAttachments?.file?.name })
      );
    }

    dataAttachments?.other_attachment_description &&
      formData.append(
        "other_attachment_description[]",
        dataAttachments?.other_attachment_description
      );
    dataAttachments?.other_attachment_comment &&
      formData.append(
        "other_attachment_comment[]",
        dataAttachments?.other_attachment_comment
      );

    dataAttachments?.other_attachment_number &&
      formData.append(
        "other_attachment_number[]",
        dataAttachments?.other_attachment_number
      );

    const date =
      dataAttachments?.other_attachment_date ||
      dataAttachments?.attachment_date;
    date && formData.append("other_attachment_date[]", date);

    dataAttachments?.attachment_date &&
      formData.append("attachment_date[]", dataAttachments?.attachment_date);
    dataAttachments?.extension &&
      formData.append("other_attachment_type[]", dataAttachments?.extension);

    if (isObject(data) && isObjectNotEmpty(data) && !payloadManual) {
      if (data) {
        Object.keys(data).forEach((documentId) => {
          if (!data[documentId].from_server) {
            formData.append(
              "attachment[" + documentId + "]",
              JSON.stringify(data[documentId])
            );
          }
        });
      }
    }

    const requestData = {
      data: payloadManual ? data : formData,
      Endpoint: envUpdateForm,
      PropertyBody: "sendFormDataContentPOST",
    };

    mutate(requestData, {
      onSuccess: handleResponseUpdateForm,
      onError: () => setIsLoading(false),
    });
    // setIsLoading(false);
  };

  const editUploadFiles = (data) => {
    const formData = new FormData();
    formData.append("id", id);

    if (data?.id) formData.append("attachment_id[]", data.id);

    if (isNonEmptyString(data?.attachment_comment)) {
      formData.append("attachment_comment[]", data.attachment_comment);
    }

    if (isNonEmptyString(data?.attachment_number)) {
      formData.append("attachment_number[]", data.attachment_number);
    }

    if (isNonEmptyString(data?.attachment_date)) {
      formData.append("attachment_date[]", data.attachment_date);
    }

    updateAndCreate(formData, true);
  };

  const handleResponseUpdateForm = (res) => {
    if (res?.data?.status?.code === 400) {
      return openAlert(res?.data?.status?.message, "error");
    }

    setOtherDocuments([]);
    setAttachment([]);
    setFiles({});

    if (res?.data?.status?.code === 200) {
      const { files, other_files } = res?.data?.data;
      const fromServer = createFromServer(res?.data?.data);
      setFiles(fromServer);
      setOtherDocuments(other_files);
      // refetch();
    }
    setIsLoading(false);
  };

  const getAuthToken = async () => {
    return token;
  };

  const cleanFile = (file) => {
    Swal.fire({
      title: `¿Estas seguro de eliminar el archivo "${
        file?.attachment_name ??
        file?.name_attachment ??
        file?.name
      }"?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1F7AC3",
      confirmButtonText: "Eliminar",
      customClass: {
        title: "class-text",
      },
    }).then((resultado) => {
      if (resultado.isConfirmed) {
        const requestData = {
          Endpoint: envDeleteOtherAttachment,
          resourceIdentifier: file?.id,
        };

        mutate(requestData, {
          onSuccess: (res) => {
            if (res?.data?.status?.code === 200) {
              setOtherDocuments(res?.data?.data?.other_files);
            }
            // refetch();
          },
        });
      }
    });
  };

  function createKey(documentReqId) {
    return "document-" + documentReqId;
  }

  function createObjectFromItem(item, index) {
    return {
      name: item.name,
      document_id: item.document_req_id,
      downloadFile: item.attachment_url,
      from_server: true,
      id: item.id,
      originalIndex: index,
      attachment_number: item.attachment_number,
      attachment_description: item.attachment_description,
      attachment_comment: item.attachment_comment,
      attachment_date: item.attachment_date,
    };
  }

  function createFromServer(data) {
    return data?.files.reduce((accumulator, item, index) => {
      const key = createKey(item.document_req_id);
      accumulator[key] = createObjectFromItem(item, index);
      return accumulator;
    }, {});
  }

  useEffect(() => {
    // if (firstRender.current) {
    //   firstRender.current = false;
    // } else {
      const fromServer = createFromServer(data);

      setFiles(Object.assign({}, files, fromServer));

      setOtherDocuments(data?.other_files);
    // }
  }, [data?.files, data?.other_files]);

  const firstRender = useRef(true);

  if (!data?.documents || !id) {
    return null;
  }

  // console.log({ DocumentManager: { files, otherDocuments } });

  return (
    <>
      <ModalGeneric
        isOpen={isOpenUpload}
        onClose={onCloseUpload}
        onOpen={onOpenUpload}
        title={`Cargar documento ${nameDocument && nameDocument}`}
        size={"lg"}
      >
        <GroupInputUpload
          onClose={onCloseUpload}
          setIsUploadActive={setIsUploadActive}
          // callBack={closeModal}
        />
      </ModalGeneric>
      <HeaderSearch>
        {data?.documents.map((document, index) => (
          <div key={index}>
            <DocumentUploader
              index={index}
              document={document}
              handleFiles={handleFiles}
              handleFilesInput={handleFilesInput}
              handleDelete={removeItemFromArr}
              files={files}
              viewBtn={viewBtn}
              idFmm={id}
              setAttachmentDescription={setAttachmentDescription}
              setAttachmentNumber={setAttachmentNumber}
              documentPreview={documentPreview}
              callBack={editUploadFiles}
              isLoading={isLoading}
            />
          </div>
        ))}
      </HeaderSearch>

      <HeaderSearch>
        <Box display={"grid"} gap={4} pt={12}>
          <EnhancedFileUploadManager
            title="Otros documentos"
            onUploadComplete={() => {}}
            handleResponseUpdateForm={handleResponseUpdateForm}
            endpointAttachments={{
              create: envUpdateForm,
            }}
            postPropertyName={"other_attachment"}
            vaporBaseUrl={environment.VAPOR_BASE_URL}
            getAuthToken={getAuthToken}
            formID={id}
          />

          <ExistingFiles
            existingFiles={otherDocuments}
            // isLoading={isPendingViewPass}
            handleDeleteFile={cleanFile}
          />
        </Box>
      </HeaderSearch>
    </>
  );
};

export default DocumentManager;
